import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon, type IconName } from "../icon/Icon";

type Size = "large" | "medium" | "small" | "xsmall";

const SIZES: Record<Size, { h: number; padX: number; font: string; icon: number }> = {
  large: { h: 40, padX: 16, font: "var(--font-size-body-2)", icon: 18 },
  medium: { h: 36, padX: 14, font: "var(--font-size-label-1)", icon: 16 },
  small: { h: 32, padX: 12, font: "var(--font-size-label-1)", icon: 16 },
  xsmall: { h: 26, padX: 10, font: "var(--font-size-label-2)", icon: 14 },
};

export interface ChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "size"> {
  active?: boolean;
  disabled?: boolean;
  size?: Size;
  iconLeft?: IconName;
  count?: number;
  children?: ReactNode;
}

/** Pill-shaped selectable chip for filters and categories. */
export function Chip({
  children,
  active = false,
  disabled = false,
  size = "medium",
  iconLeft,
  count,
  onClick,
  style,
  ...rest
}: ChipProps) {
  const s = SIZES[size] || SIZES.medium;
  const look = disabled
    ? {
        background: "var(--background-normal-normal)",
        color: "var(--label-disable)",
        boxShadow: "inset 0 0 0 1px var(--line-normal-alternative)",
      }
    : active
      ? {
          background: "var(--accent-background-light-blue)",
          color: "var(--primary-normal)",
          boxShadow: "inset 0 0 0 1px var(--primary-normal)",
        }
      : {
          background: "var(--background-normal-normal)",
          color: "var(--label-normal)",
          boxShadow: "inset 0 0 0 1px var(--line-normal-neutral)",
        };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        height: s.h,
        padding: `0 ${s.padX}px`,
        borderRadius: "var(--radius-full)",
        border: "none",
        fontFamily: "var(--font-sans)",
        fontSize: s.font,
        fontWeight: active ? 600 : 500,
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard)",
        whiteSpace: "nowrap",
        ...look,
        ...style,
      }}
      {...rest}
    >
      {iconLeft && <Icon name={iconLeft} size={s.icon} />}
      {children}
      {count != null && (
        <span style={{ color: active ? "var(--primary-normal)" : "var(--label-alternative)", fontWeight: 600 }}>
          {count}
        </span>
      )}
    </button>
  );
}

export default Chip;
